/**
 * Pitch tables and hardware constants.
 *
 * This is the file that decides whether the tracker actually sounds like the
 * machines it is imitating, so everything here is either taken verbatim from the
 * original replayers or derived from those tables by an exact, testable rule.
 */

/**
 * Paula's clock. A MOD's period is a divider on this: the chip fetches one byte
 * every `period` colour-clock ticks, so the sample rate is clock/period.
 *
 * PAL is the right default — nearly every MOD was written on a PAL machine, and
 * NTSC playback is audibly sharp.
 */
export const PAL_CLOCK = 7093789.2 / 2; // 3546894.6
export const NTSC_CLOCK = 7159090.5 / 2; // 3579545.25

/**
 * FastTracker II's reference: middle C (C-4) plays at 8363 Hz, and its Amiga
 * frequency table is scaled so that period 1712 == 8363 Hz.
 */
export const FT2_MIDDLE_C = 8363;
export const FT2_AMIGA_REF_PERIOD = 1712;
export const FT2_AMIGA_CLOCK = FT2_MIDDLE_C * FT2_AMIGA_REF_PERIOD; // 14317496

/** ProTracker's hardware period limits, used when `amigaLimits` is set. */
export const PT_MIN_PERIOD = 113;
export const PT_MAX_PERIOD = 856;

/** Notes per octave / octaves the XM note space covers. */
export const NOTES_PER_OCTAVE = 12;
export const MAX_NOTE = 96; // C-0 .. B-7

/**
 * ProTracker's period table: 16 finetune settings × 36 notes (3 octaves).
 *
 * Row order is ProTracker's own: finetunes 0..7 first, then -8..-1, which is
 * why lookups mask the finetune with 0x0F rather than offsetting it.
 */
// prettier-ignore
export const PT_PERIOD_TABLE: readonly number[][] = [
  // finetune 0
  [856,808,762,720,678,640,604,570,538,508,480,453,
   428,404,381,360,339,320,302,285,269,254,240,226,
   214,202,190,180,170,160,151,143,135,127,120,113],
  // finetune +1
  [850,802,757,715,674,637,601,567,535,505,477,450,
   425,401,379,357,337,318,300,284,268,253,239,225,
   213,201,189,179,169,159,150,142,134,126,119,113],
  // finetune +2
  [844,796,752,709,670,632,597,563,532,502,474,447,
   422,398,376,355,335,316,298,282,266,251,237,224,
   211,199,188,177,167,158,149,141,133,125,118,112],
  // finetune +3
  [838,791,746,704,665,628,592,559,528,498,470,444,
   419,395,373,352,332,314,296,280,264,249,235,222,
   209,198,187,176,166,157,148,140,132,125,118,111],
  // finetune +4
  [832,785,741,699,660,623,588,555,524,495,467,441,
   416,392,370,350,330,312,294,278,262,247,233,220,
   208,196,185,175,165,156,147,139,131,124,117,110],
  // finetune +5
  [826,779,736,694,655,619,584,551,520,491,463,437,
   413,390,368,347,328,309,292,276,260,245,232,219,
   206,195,184,174,164,155,146,138,130,123,116,109],
  // finetune +6
  [820,774,730,689,651,614,580,547,516,487,460,434,
   410,387,365,345,325,307,290,274,258,244,230,217,
   205,193,183,172,163,154,145,137,129,122,115,109],
  // finetune +7
  [814,768,725,684,646,610,575,543,513,484,457,431,
   407,384,363,342,323,305,288,272,256,242,228,216,
   204,192,181,171,161,152,144,136,128,121,114,108],
  // finetune -8
  [907,856,808,762,720,678,640,604,570,538,508,480,
   453,428,404,381,360,339,320,302,285,269,254,240,
   226,214,202,190,180,170,160,151,143,135,127,120],
  // finetune -7
  [900,850,802,757,715,675,636,601,567,535,505,477,
   450,425,401,379,357,337,318,300,284,268,253,238,
   225,212,200,189,179,169,159,150,142,134,126,119],
  // finetune -6
  [894,844,796,752,709,670,632,597,563,532,502,474,
   447,422,398,376,355,335,316,298,282,266,251,237,
   223,211,199,188,177,167,158,149,141,133,125,118],
  // finetune -5
  [887,838,791,746,704,665,628,592,559,528,498,470,
   444,419,395,373,352,332,314,296,280,264,249,235,
   222,209,198,187,176,166,157,148,140,132,125,118],
  // finetune -4
  [881,832,785,741,699,660,623,588,555,524,494,467,
   441,416,392,370,350,330,312,294,278,262,247,233,
   220,208,196,185,175,165,156,147,139,131,123,117],
  // finetune -3
  [875,826,779,736,694,655,619,584,551,520,491,463,
   437,413,390,368,347,328,309,292,276,260,245,232,
   219,206,195,184,174,164,155,146,138,130,123,116],
  // finetune -2
  [868,820,774,730,689,651,614,580,547,516,487,460,
   434,410,387,365,345,325,307,290,274,258,244,230,
   217,205,193,183,172,163,154,145,137,129,122,115],
  // finetune -1
  [862,814,768,725,684,646,610,575,543,513,484,457,
   431,407,384,363,342,323,305,288,272,256,242,228,
   216,204,192,181,171,161,152,144,136,128,121,114],
];

/**
 * Where ProTracker's 3-octave range sits in the XM note space.
 *
 * MOD periods are a quarter of FT2's: MOD's middle C is period 428, which is
 * FT2 period 1712, which is C-4 (0-based note 48). The table's first entry
 * (period 856) is therefore C-3, i.e. 0-based note 36, and the range runs
 * 36..71 (C-3..B-5).
 *
 * The two tunings are not identical — 428 on PAL Paula is 8287 Hz against FT2's
 * 8363 Hz for the same note, a 15.8 cent difference. That gap is real hardware
 * behaviour and is preserved by giving MOD playback its own clock.
 */
export const MOD_NOTE_OFFSET = 36;

/**
 * ProTracker's vibrato/tremolo sine table — a quarter-wave-symmetric ramp of 32
 * entries scaled 0..255. The replayer negates it for the second half rather than
 * storing 64 entries, exactly as the original does.
 */
// prettier-ignore
export const PT_SINE_TABLE: readonly number[] = [
    0, 24, 49, 74, 97,120,141,161,
  180,197,212,224,235,244,250,253,
  255,253,250,244,235,224,212,197,
  180,161,141,120, 97, 74, 49, 24,
];

/**
 * FastTracker II's Amiga period table: one octave spanning 907 down to 453, in
 * eighth-of-a-semitone steps, plus a terminating entry so lookups can always
 * interpolate towards index+1.
 *
 * It is generated rather than pasted because it is exactly the ProTracker table
 * read column-wise: entry [8*s + f] is the ProTracker period for note `s` at
 * finetune `f - 8`. `verifyAmigaTable()` asserts that relationship holds.
 */
export const FT2_AMIGA_PERIODS: Int32Array = buildAmigaPeriodTable();

function buildAmigaPeriodTable(): Int32Array {
  const table = new Int32Array(12 * 8 + 1);
  for (let semitone = 0; semitone < 12; semitone++) {
    for (let sub = 0; sub < 8; sub++) {
      // finetune -8..-1 live in ProTracker rows 8..15.
      const row = 8 + sub;
      table[semitone * 8 + sub] = PT_PERIOD_TABLE[row][semitone];
    }
  }
  // The final entry closes the octave: note 11 at finetune 0.
  table[96] = PT_PERIOD_TABLE[0][11];
  return table;
}

/**
 * Sanity check used by the test suite: the generated table must be monotonically
 * descending and must span exactly one octave (within the rounding the original
 * integer table carries).
 */
export function verifyAmigaTable(): { monotonic: boolean; octaveRatio: number } {
  let monotonic = true;
  for (let i = 1; i < FT2_AMIGA_PERIODS.length; i++) {
    if (FT2_AMIGA_PERIODS[i] >= FT2_AMIGA_PERIODS[i - 1]) monotonic = false;
  }
  return {
    monotonic,
    octaveRatio: FT2_AMIGA_PERIODS[0] / FT2_AMIGA_PERIODS[96],
  };
}

/**
 * Look up an Amiga period with fractional finetune resolution.
 *
 * `index` is in eighth-of-a-semitone units measured from the table's origin
 * (which is one semitone below C). Values past the end of the table wrap into
 * the next octave, halving as they go, so notes near B with positive finetune
 * slide smoothly into the octave above instead of clamping.
 */
export function amigaTableLookup(index: number): number {
  let i = index;
  let scale = 1;
  while (i > 96) {
    i -= 96;
    scale *= 0.5;
  }
  while (i < 0) {
    i += 96;
    scale *= 2;
  }
  const lo = Math.floor(i);
  const frac = i - lo;
  const a = FT2_AMIGA_PERIODS[lo];
  const b = FT2_AMIGA_PERIODS[Math.min(lo + 1, 96)];
  return (a + (b - a) * frac) * scale;
}

/**
 * FastTracker II Amiga-mode period for a 0-based note (0 = C-0) and finetune.
 *
 * Scaled so C-4 (note 48) at finetune 0 yields period 1712, which is 8363 Hz
 * through `FT2_AMIGA_CLOCK`.
 */
export function ft2AmigaPeriod(note: number, finetune: number): number {
  const octave = Math.floor(note / NOTES_PER_OCTAVE);
  const semitone = note - octave * NOTES_PER_OCTAVE;
  const index = semitone * 8 + 8 + finetune / 16;
  return amigaTableLookup(index) * Math.pow(2, 5 - octave);
}

/**
 * FastTracker II linear-mode period. One semitone is 64 units; finetune spans
 * ±128 for ±1 semitone, hence the halving.
 */
export function ft2LinearPeriod(note: number, finetune: number): number {
  return 10 * 12 * 16 * 4 - note * 16 * 4 - finetune / 2;
}

/** Playback rate in Hz for an FT2 linear-mode period. */
export function ft2LinearFrequency(period: number): number {
  return FT2_MIDDLE_C * Math.pow(2, (4608 - period) / 768);
}

/** Playback rate in Hz for an FT2 Amiga-mode period. */
export function ft2AmigaFrequency(period: number): number {
  return FT2_AMIGA_CLOCK / period;
}

/**
 * ProTracker period for a 0-based XM note, using the MOD note offset so that
 * note 24 (C-2) is the table's first entry.
 */
export function ptPeriod(note: number, finetune: number): number {
  const index = note - MOD_NOTE_OFFSET;
  const row = (finetune >> 4) & 0x0f; // MOD finetune is stored ×16
  const table = PT_PERIOD_TABLE[row];
  if (index < 0) {
    // Below the table: keep doubling from the matching note an octave up.
    let n = index;
    let scale = 1;
    while (n < 0) {
      n += NOTES_PER_OCTAVE;
      scale *= 2;
    }
    return table[n] * scale;
  }
  if (index >= table.length) {
    // Above the table: halve down from the top octave.
    let scale = 1;
    let n = index;
    while (n >= table.length) {
      n -= NOTES_PER_OCTAVE;
      scale *= 0.5;
    }
    return table[n] * scale;
  }
  return table[index];
}

/** Playback rate in Hz for a ProTracker period on PAL hardware. */
export function ptFrequency(period: number, clock = PAL_CLOCK): number {
  return clock / period;
}

/**
 * Convert a raw MOD period to a 0-based note index by finding the nearest entry
 * in the finetune-0 table. Trackers only ever emit table values, but files
 * mangled by conversion tools can carry anything, so this never fails.
 */
export function periodToNote(period: number): number {
  if (period <= 0) return -1;
  const table = PT_PERIOD_TABLE[0];
  let best = 0;
  let bestDist = Infinity;
  for (let i = 0; i < table.length; i++) {
    const dist = Math.abs(Math.log(table[i] / period));
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  }
  return best + MOD_NOTE_OFFSET;
}

/** True when `period` is not an exact entry of the finetune-0 table. */
export function isOffTablePeriod(period: number): boolean {
  return !PT_PERIOD_TABLE[0].includes(period);
}

/** Note names for display, indexed by semitone. */
export const NOTE_NAMES = ['C-', 'C#', 'D-', 'D#', 'E-', 'F-', 'F#', 'G-', 'G#', 'A-', 'A#', 'B-'];

/** Render a note value (1..96, 97 = key off) the way a tracker displays it. */
export function formatNote(note: number): string {
  if (note === 0) return '...';
  if (note === 97) return '===';
  const n = note - 1;
  return NOTE_NAMES[n % 12] + Math.floor(n / 12);
}

/**
 * Tempo/speed: a "tick" is 2.5/BPM seconds, and `speed` ticks make a row. This
 * is the relationship every tracker since Soundtracker has used.
 */
export function samplesPerTick(sampleRate: number, bpm: number): number {
  return (sampleRate * 2.5) / bpm;
}
